import { Link } from "react-router-dom";
import { languageChips, specialtyChips } from "../../lib/agent-descriptors";
import type { ProBrowseCard as CardData } from "../../lib/marketplace-discovery-api";
import { AgentAvatar } from "./AgentAvatar";
import { SignalsLine } from "./SignalsLine";
import { StandingOfferRollup } from "./StandingOfferRollup";

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function compactMoney(cents: number) {
  const dollars = cents / 100;
  if (dollars < 1000) return money.format(dollars);
  return `$${Number.parseFloat((dollars / 1000).toFixed(1))}k`;
}

export function ProBrowseCard({
  agent,
  compact = false,
  onClick,
  profileHref,
}: {
  agent: CardData;
  /**
   * Plan 435 M3 (mfh-agent-preview): the Home agent-preview tile. Hides the
   * blurb, match-reason chip, and `StandingOfferRollup` (the browse page's
   * own full-detail context); keeps avatar, name, rating, brokerage/
   * distance, the accepting line, savings, and a single full-width "View
   * profile" link — never "Request" (design §4.1: one decision per
   * surface; Request lives on the profile). Same `marketplace-surface`
   * fill, no other visual change.
   */
  compact?: boolean;
  onClick?: () => void;
  profileHref: string;
}) {
  const name = agent.identity.display_name ?? "Agent";
  const firstName = name.split(/\s+/)[0] || "agent";
  const displayReasons = agent.match_reasons
    .filter((reason) => reason !== agent.distance_label)
    .filter((reason) => !/accepting( new clients)?/i.test(reason))
    .map((reason) =>
      reason === "Has an offer at your tier" ? "Offer at your tier" : reason,
    );
  const specialties = specialtyChips(agent.identity);
  const languages = languageChips(agent.identity);
  const hasFooter =
    (!compact && Boolean(agent.standing_offer)) || agent.accepting_new_clients;
  const content = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <AgentAvatar
            className="size-11 shrink-0 rounded-full"
            fallbackClassName="bg-primary text-sm font-bold text-primary-foreground"
            imageSize={88}
            name={name}
            photoUrl={agent.identity.photo_url}
            quality={85}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-foreground">{name}</h2>
              {agent.avg_rating != null && (
                <span
                  aria-label={`${agent.avg_rating.toFixed(1)} out of 5, ${agent.review_count} reviews`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-foreground"
                  data-testid="browse-rating-chip"
                >
                  <span aria-hidden="true" className="text-primary">
                    ★
                  </span>
                  {`${agent.avg_rating.toFixed(1)} (${agent.review_count})`}
                </span>
              )}
              {agent.buyer_pro_state === "requested" && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
                  Requested
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {agent.identity.brokerage}
              {agent.distance_label ? ` · ${agent.distance_label}` : ""}
            </p>
            <SignalsLine
              className="mt-0.5 text-xs text-muted-foreground"
              signals={agent.signals}
              testId="browse-signals-line"
            />
            <p
              className={`mt-0.5 text-xs font-semibold ${agent.accepting_new_clients ? "text-success" : "text-muted-foreground"}`}
            >
              {agent.accepting_new_clients
                ? "Accepting new clients"
                : "Currently paused"}
            </p>
          </div>
        </div>
        {agent.estimated_savings && (
          <div className="shrink-0 text-right" data-testid="browse-savings">
            <p className="text-xs font-semibold text-muted-foreground">
              Savings
            </p>
            <p className="tabular-nums text-xl font-bold text-primary">
              {compactMoney(agent.estimated_savings.cents)}
            </p>
            <p className="max-w-24 text-[10px] leading-tight text-muted-foreground">
              Est. on {compactMoney(agent.estimated_savings.basis_cents)}
            </p>
          </div>
        )}
      </div>
      {!compact && agent.identity.blurb && (
        <p className="mt-3 text-wrap-pretty text-sm text-foreground">
          {agent.identity.blurb}
        </p>
      )}
      {!compact && (specialties.length > 0 || languages.length > 0) && (
        <div
          className="mt-3 flex flex-wrap gap-1.5"
          data-testid="browse-descriptor-chips"
        >
          {specialties.map((chip) => (
            <span
              className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-foreground"
              data-testid="browse-specialty-chip"
              key={`s-${chip.code}`}
            >
              {chip.label}
            </span>
          ))}
          {languages.map((chip) => (
            <span
              className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground"
              data-testid="browse-language-chip"
              key={`l-${chip.code}`}
            >
              {chip.label}
            </span>
          ))}
        </div>
      )}
      {!compact && displayReasons.length > 0 && (
        <p
          className="mt-3 inline-flex max-w-full flex-wrap items-center gap-x-1 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
          data-testid="browse-match-reasons"
        >
          <span aria-hidden="true">✓</span>
          <span>{displayReasons.join(" · ")}</span>
        </p>
      )}
      {hasFooter && (
        <div className="mt-auto" data-testid="browse-card-footer">
        {!compact && agent.standing_offer && (
          <div className="mt-3">
            <StandingOfferRollup offer={agent.standing_offer} />
          </div>
        )}
        {agent.accepting_new_clients &&
          (compact ? (
            <div className="mt-4">
              <Link
                className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-border bg-popover px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                onClick={onClick}
                to={profileHref}
              >
                View profile
              </Link>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Link
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-popover px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                onClick={onClick}
                to={profileHref}
              >
                View profile
              </Link>
              {agent.standing_offer && (
                <Link
                  className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                  onClick={onClick}
                  to={`${profileHref}#request-agent`}
                >
                  Request {firstName}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
  // Equal-height cards: the root fills its grid row (`h-full`) and is a
  // vertical flex column; the footer (standing offer + CTAs) takes `mt-auto`
  // so any spare height shows as white space above it, never as stretched
  // content. Applies to compact Home tiles too (same row misalignment).
  const className = "marketplace-surface flex h-full flex-col p-4";
  if (!agent.accepting_new_clients) {
    return (
      <div
        aria-disabled="true"
        className={`${className} opacity-75`}
        data-compact={compact || undefined}
        data-testid="pro-browse-card-paused"
      >
        {content}
      </div>
    );
  }
  // The card itself is not a control — its two footer links are. No press
  // scale or hover lift here: those read as "the whole card is clickable",
  // and clicking the card does nothing.
  return (
    <div className={className} data-compact={compact || undefined} data-testid="pro-browse-card">
      {content}
    </div>
  );
}
